import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthenticateWithRedirectCallback, useUser } from '@clerk/clerk-react';
import { Spinner } from '../../shared/ui/Spinner';
import { Button } from '../../shared/ui';
import { useRecordConsent } from '../../features/users/hooks';
import { PENDING_CONSENT_KEY } from './consent-storage';

// AuthenticateWithRedirectCallback exposes no onError/onTimeout prop — if the redirect exchange
// hangs (e.g. an account-linking conflict Clerk can't resolve), this page would otherwise spin
// forever with no way out. This is a client-side fallback: if we're still here after 10s with no
// session, assume it's stuck and offer a way back rather than trusting Clerk to always resolve.
const STUCK_TIMEOUT_MS = 10_000;

export function SSOCallback() {
  const { isLoaded, isSignedIn } = useUser();
  const recordConsent = useRecordConsent();
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setStuck(true), STUCK_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, []);

  // Google sign-up redirects through this page before landing on /app/requests — this is the
  // first point we have a live session, so it's where the consent checked on the signup page
  // actually gets recorded server-side.
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    const pendingVersion = sessionStorage.getItem(PENDING_CONSENT_KEY);
    if (!pendingVersion) return;
    sessionStorage.removeItem(PENDING_CONSENT_KEY);
    recordConsent.mutate(pendingVersion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, isSignedIn]);

  if (stuck && !isSignedIn) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-white text-lg font-bold">This is taking longer than expected</p>
        <p className="text-white/40 text-sm max-w-sm">
          We couldn't finish signing you in. This can happen if the sign-in was cancelled or timed out.
        </p>
        <Button to="/login" variant="marketing-primary" size="md" className="!text-sm mt-2">
          Back to sign in
        </Button>
        <Link to="/#contact" className="text-white/40 text-xs hover:text-white transition-colors">
          Still stuck? Contact us
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center gap-4">
      <Spinner size="lg" />
      <p className="text-white/40 text-sm">Finishing sign-in...</p>
      <AuthenticateWithRedirectCallback
        signInForceRedirectUrl="/app/requests"
        signUpForceRedirectUrl="/app/requests"
      />
    </div>
  );
}
